import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CalendarDays } from 'lucide-react';
import { type ComponentProps, useRef, useState } from 'react';

import { cn } from '@/design-system/lib/cn';

import { Calendar } from './Calendar';
import { displayToIso, isoToDisplay, maskDate, todayIso } from './dates';

type InputAttrs = Omit<
  ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'type' | 'min' | 'max' | 'inputMode'
>;

export type DateFieldProps = InputAttrs & {
  /** ISO (`aaaa-mm-dd`) ou vazio. */
  value: string;
  /**
   * Recebe o ISO quando a data está completa e válida; vazio quando não está. Fora de
   * `min`/`max` a data chega (como no `<input type="date">`) e o campo fica marcado — a
   * mensagem é de quem usa; o calendário nem oferece esses dias.
   */
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  invalid?: boolean;
  /** Intervalo destacado no calendário (usado pelo `DateRangeField`). */
  range?: { from: string | null; to: string | null };
  /** Mês que o calendário abre quando o campo está vazio. */
  defaultMonth?: string;
  /** Controle do calendário por fora (o `DateRangeField` abre o "Até" depois do "De"). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Chamado só quando a data vem do calendário (não da digitação). */
  onPick?: (iso: string) => void;
};

/**
 * Campo de data: digita-se `dd/mm/aaaa` com o teclado numérico (a máscara põe as barras) ou
 * escolhe-se no calendário do botão ao lado, num popover. Substitui o `<input type="date">`,
 * que no celular abre o seletor do sistema, diferente em cada aparelho. O valor por fora é
 * sempre ISO; data incompleta ou inválida vira vazio.
 */
export function DateField({
  value,
  onChange,
  min,
  max,
  invalid = false,
  range,
  defaultMonth,
  open: openProp,
  onOpenChange,
  onPick,
  disabled,
  className,
  placeholder = 'dd/mm/aaaa',
  onBlur,
  ...props
}: DateFieldProps) {
  const [text, setText] = useState(() => isoToDisplay(value));
  const [openState, setOpenState] = useState(false);
  const [emitted, setEmitted] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  // Valor trocado por fora (limpar o formulário, outro registro): o texto acompanha.
  if (value !== emitted) {
    setEmitted(value);
    setText(isoToDisplay(value));
  }

  const emit = (iso: string) => {
    setEmitted(iso);
    onChange(iso);
  };

  const iso = displayToIso(text);
  const outside = !!iso && ((!!min && iso < min) || (!!max && iso > max));
  const incomplete = text.length > 0 && !iso;

  const type = (raw: string) => {
    const next = maskDate(raw);
    setText(next);
    emit(displayToIso(next) ?? '');
  };

  const pick = (picked: string) => {
    setText(isoToDisplay(picked));
    emit(picked);
    onPick?.(picked);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen} modal>
      <PopoverPrimitive.Anchor asChild>
        <div className={cn('relative w-full min-w-0', className)}>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            data-slot="input"
            maxLength={10}
            placeholder={placeholder}
            disabled={disabled}
            value={text}
            aria-invalid={invalid || outside || undefined}
            onChange={(e) => type(e.target.value)}
            onBlur={(e) => {
              // Saiu com a data pela metade: limpa, para não parecer salva.
              if (incomplete) setText('');
              onBlur?.(e);
            }}
            className={cn(
              'h-10 w-full min-w-0 rounded-control border bg-white pr-11 pl-3 text-control text-ink max-md:text-small',
              'placeholder:text-ink-placeholder disabled:bg-surface-subtle disabled:text-ink-subtle',
              invalid || outside ? 'border-status-conflict' : 'border-line-input',
              open && 'border-primary-focus ring-3 ring-primary-focus/15'
            )}
            {...props}
          />
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Escolher no calendário"
              className="absolute top-1/2 right-1 grid size-8 -translate-y-1/2 place-items-center rounded-control-sm text-ink-subtle hover:bg-surface-muted hover:text-primary disabled:pointer-events-none disabled:opacity-50"
            >
              <CalendarDays aria-hidden className="size-4.5 stroke-[1.8]" />
            </button>
          </PopoverPrimitive.Trigger>
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          aria-label="Calendário"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'z-[90] rounded-block border border-line-input bg-white p-2 shadow-popover outline-none',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
          )}
        >
          <Calendar
            focusOnMount
            value={value || null}
            range={range}
            min={min}
            max={max}
            defaultMonth={defaultMonth}
            onSelect={pick}
          />
          <div className="mt-1 flex items-center justify-between border-t border-line-divider px-1 pt-2">
            <button
              type="button"
              onClick={() => {
                setText('');
                emit('');
                setOpen(false);
                inputRef.current?.focus();
              }}
              className="h-8 rounded-control-sm px-2.5 text-meta font-semibold text-ink-muted hover:bg-surface-muted"
            >
              Limpar
            </button>
            <button
              type="button"
              disabled={(!!min && todayIso() < min) || (!!max && todayIso() > max)}
              onClick={() => pick(todayIso())}
              className="h-8 rounded-control-sm px-2.5 text-meta font-semibold text-primary hover:bg-primary-faint disabled:opacity-40"
            >
              Hoje
            </button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
